import { memo, useCallback, useMemo, useState } from 'react';
import { Text } from '@library';
import useSearchParamFilterHelper from '../../hooks/useSearchParamFilterHelper';
import useProductFilterCount from '../../queries/useProductFilterCount';
import processAttributeFilterValues from '../../util/processAttributeFilterValues';
import preSortedAttributes from '../../util/preSortedAttributes';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import styles from './Filter.module.scss';
import FilterWrapper from './FilterWrapper';
import FilterCheckbox from './FilterCheckbox';

export const useFilteredValues = ({
    search,
    getCount,
    field,
    filterValues,
}: {
    search: string;
    getCount(id: string): number;
    field: string;
    filterValues: {
        value: string;
        valueLabel?: string;
    }[];
}): [{ value: string; valueLabel: string }[], number] => {
    return useMemo(() => {
        const [result, valuesCount] = processAttributeFilterValues({
            values: filterValues.map(item => ({
                value: item.value,
                label: item.valueLabel || item.value,
            })),
            searchTerm: search,
            getCount,
        });

        if (!preSortedAttributes.includes(field)) {
            result.sort((a, b) =>
                a.valueLabel
                    .toLowerCase()
                    .localeCompare(b.valueLabel.toLowerCase())
            );
        }

        return [result, valuesCount];
    }, [search, getCount, field, filterValues]);
};

const Filter = withErrorBoundary(
    memo(
        ({
            name,
            field,
            defaultExpand,
        }: {
            name: string;
            field: string;
            defaultExpand?: boolean;
        }) => {
            const { updateCurrentValues, selectedValues } =
                useSearchParamFilterHelper()(field);
            const [search, setSearch] = useState('');
            const { productFilterValues } = useProductFilterCount({
                filterField: field,
            });

            const getCount = useCallback(
                (value: string) =>
                    productFilterValues.find(item => item.value === value)
                        ?.total ?? 0,
                [productFilterValues]
            );

            const [filteredValues, count] = useFilteredValues({
                search,
                getCount,
                field,
                filterValues: productFilterValues,
            });

            if (count === 0) {
                return null;
            }

            return (
                <FilterWrapper
                    name={name}
                    setSearch={setSearch}
                    defaultExpand={defaultExpand}
                    items={filteredValues.map(({ value, valueLabel }) => ({
                        render: setShowFullList => (
                            <FilterItem
                                selectedValues={selectedValues}
                                getCount={getCount}
                                key={`filter_item_${value}`}
                                updateCurrentValues={(
                                    newValue: string,
                                    isRemove?: boolean,
                                    isReplace?: boolean
                                ) => {
                                    setShowFullList(false);
                                    updateCurrentValues(
                                        newValue,
                                        isRemove,
                                        isReplace
                                    );
                                }}
                                value={value}
                                label={valueLabel}
                            />
                        ),
                        isSelected: selectedValues.includes(value),
                    }))}
                />
            );
        }
    )
);

const FilterItem = memo(
    ({
        updateCurrentValues,
        selectedValues,
        value,
        label,
        getCount,
    }: {
        updateCurrentValues(
            value: string,
            isRemove?: boolean,
            isReplace?: boolean
        ): void;
        selectedValues: string[];
        value: string;
        label?: string;
        getCount(value: string): number;
    }) => (
        <FilterCheckbox
            updateCurrentValues={updateCurrentValues}
            selectedValues={selectedValues}
            value={value}
        >
            <div className={styles.value} data-content={value}>
                <Text secondary>{label || value}</Text>
            </div>
            &nbsp;&nbsp;
            <div className={styles.count}>{getCount(value)}</div>
        </FilterCheckbox>
    )
);

export default Filter;
